import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  JOB_LABELS,
  deleteBookingFlowTest,
  getContentInventory,
  listBookingFlowTests,
  listCatalogChanges,
  listJobRuns,
  listPartnerDeliveries,
  retryPartnerDelivery,
  saveBookingFlowTest,
} from "@/lib/system-monitor.functions";
import { listOpsLog } from "@/lib/ops-log.functions";

export const Route = createFileRoute("/_authenticated/admin/monitoring")({
  head: () => ({
    meta: [
      { title: "Admin · Monitoreo del sistema — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MonitoringPage,
});

function fmtDate(v?: string | null) {
  return v ? new Date(v).toLocaleString("es-MX") : "—";
}
function fmtDuration(ms?: number | null) {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}
const STATUS_ES: Record<string, string> = {
  running: "En ejecución",
  success: "Correcta",
  error: "Fallida",
};
const DELIVERY_ES: Record<string, string> = {
  pending: "Pendiente",
  failed: "En reintento",
  success: "Entregado",
  dead: "Fallido definitivo",
};

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "success"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : status === "error" || status === "dead"
        ? "bg-red-500/10 text-red-600 dark:text-red-400"
        : "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_ES[status] ?? DELIVERY_ES[status] ?? status}
    </span>
  );
}

function MonitoringPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-4 py-10 space-y-6">
        <AdminNav />
        <header>
          <h1 className="font-display text-3xl font-semibold">Monitoreo del sistema</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Estado de las revisiones automáticas, cambios detectados, avisos a socios, contenido
            pendiente y pruebas del flujo de reserva — todo en una sola pantalla.
          </p>
        </header>

        <Tabs defaultValue="revisiones">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="revisiones">Revisiones automáticas</TabsTrigger>
            <TabsTrigger value="cambios">Cambios detectados</TabsTrigger>
            <TabsTrigger value="avisos">Avisos a socios</TabsTrigger>
            <TabsTrigger value="operativo">Registro operativo</TabsTrigger>
            <TabsTrigger value="contenido">Contenido pendiente</TabsTrigger>
            <TabsTrigger value="pruebas">Pruebas del flujo de reserva</TabsTrigger>
          </TabsList>

          <TabsContent value="revisiones" className="pt-6">
            <JobsTab />
          </TabsContent>
          <TabsContent value="cambios" className="pt-6">
            <ChangesTab />
          </TabsContent>
          <TabsContent value="avisos" className="pt-6">
            <DeliveriesTab />
          </TabsContent>
          <TabsContent value="operativo" className="pt-6">
            <OpsTab />
          </TabsContent>
          <TabsContent value="contenido" className="pt-6">
            <ContentTab />
          </TabsContent>
          <TabsContent value="pruebas" className="pt-6">
            <TestsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ------------------------------------------------------- revisiones ------- */

function JobsTab() {
  const load = useServerFn(listJobRuns);
  const q = useQuery({
    queryKey: ["monitor-jobs"],
    queryFn: () => load({ data: { limit: 60 } }),
    refetchInterval: 30_000,
  });

  const latest = q.data?.latest ?? [];
  const runs = q.data?.runs ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2">
        {latest.map(({ jobKey, run }) => {
          const meta = JOB_LABELS[jobKey];
          return (
            <div key={jobKey} className="rounded-xl border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{meta?.label ?? jobKey}</div>
                  <div className="text-xs text-muted-foreground">{meta?.cadence}</div>
                </div>
                {run ? <StatusPill status={run.status} /> : (
                  <span className="text-xs text-muted-foreground">Sin ejecuciones aún</span>
                )}
              </div>
              {run && (
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">Última ejecución</dt>
                  <dd>{fmtDate(run.started_at)}</dd>
                  <dt className="text-muted-foreground">Duración</dt>
                  <dd>{fmtDuration(run.duration_ms)}</dd>
                  <dt className="text-muted-foreground">Cambios detectados</dt>
                  <dd>{run.changes_detected}</dd>
                  <dt className="text-muted-foreground">Avisos enviados</dt>
                  <dd>{run.notifications_sent}</dd>
                  <dt className="text-muted-foreground">Reintentos</dt>
                  <dd>{run.retries}</dd>
                  <dt className="text-muted-foreground">Avisos pendientes</dt>
                  <dd>{run.pending_notifications}</dd>
                </dl>
              )}
              {run?.error && (
                <p className="mt-2 rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-400">
                  {run.error}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-medium">Historial de ejecuciones</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              Avisos abiertos ahora: {q.data?.pendingDeliveries ?? 0}
            </span>
            <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
              Refrescar
            </Button>
          </div>
        </div>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Todavía no hay ejecuciones registradas. Se registran a partir de la próxima revisión.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Inicio</th>
                  <th className="px-3 py-2">Revisión</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Duración</th>
                  <th className="px-3 py-2">Cambios</th>
                  <th className="px-3 py-2">Avisos</th>
                  <th className="px-3 py-2">Reintentos</th>
                  <th className="px-3 py-2">Pendientes</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDate(r.started_at)}</td>
                    <td className="px-3 py-2">{JOB_LABELS[r.job_key]?.label ?? r.job_key}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2">{fmtDuration(r.duration_ms)}</td>
                    <td className="px-3 py-2">{r.changes_detected}</td>
                    <td className="px-3 py-2">{r.notifications_sent}</td>
                    <td className="px-3 py-2">{r.retries}</td>
                    <td className="px-3 py-2">{r.pending_notifications}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- cambios ------- */

function ChangesTab() {
  const load = useServerFn(listCatalogChanges);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tour, setTour] = useState("");
  const [type, setType] = useState<"" | "availability" | "price">("");

  const q = useQuery({
    queryKey: ["monitor-changes", from, to, tour, type],
    queryFn: () =>
      load({
        data: {
          from: from || undefined,
          to: to || undefined,
          tour: tour || undefined,
          changeType: type || undefined,
          limit: 300,
        },
      }),
  });

  const changes = q.data?.changes ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-muted-foreground">
          Desde
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" />
        </label>
        <label className="text-xs text-muted-foreground">
          Hasta
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" />
        </label>
        <label className="text-xs text-muted-foreground">
          Tour
          <Input
            placeholder="Nombre del tour"
            value={tour}
            onChange={(e) => setTour(e.target.value)}
            className="h-9"
          />
        </label>
        <select
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
          value={type}
          onChange={(e) => setType(e.target.value as "" | "availability" | "price")}
        >
          <option value="">Todos los cambios</option>
          <option value="availability">Disponibilidad</option>
          <option value="price">Precio</option>
        </select>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
          Aplicar
        </Button>
      </div>

      {changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No hay cambios registrados con estos filtros.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Detectado</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Tour</th>
                <th className="px-3 py-2">Fecha afectada</th>
                <th className="px-3 py-2">Antes</th>
                <th className="px-3 py-2">Ahora</th>
                <th className="px-3 py-2">Avisos</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c: any) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-2">
                    {c.change_type === "price" ? "Precio" : "Disponibilidad"}
                  </td>
                  <td className="px-3 py-2">{c.tour_title ?? c.tour_id}</td>
                  <td className="px-3 py-2">{c.affected_date ?? "—"}</td>
                  <td className="px-3 py-2">{c.previous_value ?? "—"}</td>
                  <td className="px-3 py-2 font-medium">
                    {c.new_value}
                    {c.change_type === "price" && c.currency ? ` ${c.currency}` : ""}
                  </td>
                  <td className="px-3 py-2">{c.deliveries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- avisos ------- */

function DeliveriesTab() {
  const load = useServerFn(listPartnerDeliveries);
  const retry = useServerFn(retryPartnerDelivery);
  const qc = useQueryClient();
  const [status, setStatus] = useState<"" | "pending" | "failed" | "success" | "dead">("");

  const q = useQuery({
    queryKey: ["monitor-deliveries", status],
    queryFn: () => load({ data: { status: status || undefined, limit: 150 } }),
    refetchInterval: 60_000,
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => retry({ data: { id } }),
    onSuccess: (res) => {
      toast[res.ok ? "success" : "error"](
        res.ok ? "Aviso reenviado correctamente." : `El reenvío falló (${res.status ?? res.error}).`,
      );
      qc.invalidateQueries({ queryKey: ["monitor-deliveries"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const counts = q.data?.counts ?? { pending: 0, failed: 0, success: 0, dead: 0 };
  const rows = q.data?.deliveries ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(["pending", "failed", "success", "dead"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setStatus(status === k ? "" : k)}
            className={`rounded-xl border p-3 text-left ${
              status === k ? "border-primary/50 bg-primary/5" : "border-border"
            }`}
          >
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {DELIVERY_ES[k]}
            </div>
            <div className="mt-1 text-2xl font-semibold">{counts[k] ?? 0}</div>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay avisos con este filtro.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Evento</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Intentos</th>
                <th className="px-3 py-2">Respuesta</th>
                <th className="px-3 py-2">Próximo reintento</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d: any) => (
                <tr key={d.id} className="border-t border-border">
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDate(d.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.event_type}</td>
                  <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                  <td className="px-3 py-2">{d.attempts}</td>
                  <td className="px-3 py-2 text-xs">{d.response_status ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(d.next_retry_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {d.status !== "success" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retryMut.isPending}
                        onClick={() => retryMut.mutate(d.id)}
                      >
                        Reintentar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------- operativo ------- */

function OpsTab() {
  const load = useServerFn(listOpsLog);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");

  const q = useQuery({
    queryKey: ["monitor-ops", category, severity],
    queryFn: () =>
      load({
        data: {
          category: (category || undefined) as never,
          severity: (severity || undefined) as never,
          limit: 200,
        },
      }),
    refetchInterval: 15_000,
  });

  const entries = q.data?.entries ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <select
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          <option value="auth">Accesos</option>
          <option value="bokun">Bókun</option>
          <option value="sync">Sincronización</option>
          <option value="system">Sistema</option>
        </select>
        <select
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="">Todas las severidades</option>
          <option value="error">Error</option>
          <option value="warn">Aviso</option>
          <option value="info">Información</option>
        </select>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
          Refrescar
        </Button>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay eventos con este filtro.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Severidad</th>
                <th className="px-3 py-2">Categoría</th>
                <th className="px-3 py-2">Código</th>
                <th className="px-3 py-2">Mensaje</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground">
                    {fmtDate(e.ts)}
                  </td>
                  <td className="px-3 py-2 text-xs">{e.severity}</td>
                  <td className="px-3 py-2 text-xs">{e.category}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.code}</td>
                  <td className="px-3 py-2">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------- contenido ------- */

function ContentTab() {
  const load = useServerFn(getContentInventory);
  const q = useQuery({ queryKey: ["monitor-content"], queryFn: () => load({}) });
  const groups = q.data?.groups ?? [];

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Calculando…</p>;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {groups
        .slice()
        .sort((a, b) => a.priority - b.priority || b.missing - a.missing)
        .map((g) => (
          <div key={g.key} className="rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{g.label}</div>
                <div className="text-xs text-muted-foreground">
                  Prioridad {g.priority} · {g.missing} de {g.total} pendientes
                </div>
              </div>
              <Link
                to={g.href}
                className="rounded-full border border-border px-3 py-1 text-xs hover:bg-accent"
              >
                Completar
              </Link>
            </div>
            {g.items.length > 0 && (
              <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-sm">
                {g.items.map((it) => (
                  <li key={it.id} className="flex justify-between gap-2 border-b border-border/50 pb-1">
                    <span>{it.label}</span>
                    {it.hint && <span className="text-xs text-muted-foreground">{it.hint}</span>}
                  </li>
                ))}
              </ul>
            )}
            {g.missing === 0 && (
              <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">Todo completo.</p>
            )}
          </div>
        ))}
    </div>
  );
}

/* ---------------------------------------------------------- pruebas ------- */

const emptyTest = {
  tested_at: "",
  tour_label: "",
  result: "pass" as "pass" | "fail" | "partial",
  amount: "",
  currency: "MXN",
  payment_status: "",
  booking_status: "",
  refunded: false,
  errors: "",
  notes: "",
};

function TestsTab() {
  const load = useServerFn(listBookingFlowTests);
  const save = useServerFn(saveBookingFlowTest);
  const del = useServerFn(deleteBookingFlowTest);
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...emptyTest });

  const q = useQuery({ queryKey: ["monitor-tests"], queryFn: () => load({}) });

  const saveMut = useMutation({
    mutationFn: () =>
      save({
        data: {
          tested_at: form.tested_at || undefined,
          tour_label: form.tour_label,
          result: form.result,
          amount: form.amount ? Number(form.amount) : null,
          currency: form.currency || "MXN",
          payment_status: form.payment_status || null,
          booking_status: form.booking_status || null,
          refunded: form.refunded,
          errors: form.errors || null,
          notes: form.notes || null,
        },
      }),
    onSuccess: () => {
      toast.success("Prueba registrada.");
      setForm({ ...emptyTest });
      qc.invalidateQueries({ queryKey: ["monitor-tests"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitor-tests"] }),
  });

  const tests = q.data?.tests ?? [];

  return (
    <div className="space-y-6">
      <form
        className="grid gap-3 rounded-xl border border-border p-4 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.tour_label.trim()) {
            toast.error("Indica el tour probado.");
            return;
          }
          saveMut.mutate();
        }}
      >
        <label className="text-xs text-muted-foreground">
          Fecha de la prueba
          <Input
            type="datetime-local"
            value={form.tested_at}
            onChange={(e) => setForm({ ...form, tested_at: e.target.value })}
          />
        </label>
        <label className="text-xs text-muted-foreground md:col-span-2">
          Tour probado
          <Input
            value={form.tour_label}
            onChange={(e) => setForm({ ...form, tour_label: e.target.value })}
            placeholder="Nombre del tour o producto"
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Resultado
          <select
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm"
            value={form.result}
            onChange={(e) => setForm({ ...form, result: e.target.value as typeof form.result })}
          >
            <option value="pass">Correcta</option>
            <option value="partial">Con observaciones</option>
            <option value="fail">Fallida</option>
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Importe
          <Input
            type="number"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Moneda
          <Input
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Estado del pago
          <Input
            value={form.payment_status}
            onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
            placeholder="pagado, fallido…"
          />
        </label>
        <label className="text-xs text-muted-foreground">
          Estado de la reserva
          <Input
            value={form.booking_status}
            onChange={(e) => setForm({ ...form, booking_status: e.target.value })}
            placeholder="confirmada, pendiente…"
          />
        </label>
        <label className="flex items-center gap-2 pt-5 text-sm">
          <input
            type="checkbox"
            checked={form.refunded}
            onChange={(e) => setForm({ ...form, refunded: e.target.checked })}
          />
          Hubo reembolso
        </label>
        <label className="text-xs text-muted-foreground md:col-span-3">
          Errores encontrados
          <Textarea
            rows={2}
            value={form.errors}
            onChange={(e) => setForm({ ...form, errors: e.target.value })}
          />
        </label>
        <label className="text-xs text-muted-foreground md:col-span-3">
          Observaciones
          <Textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <div className="md:col-span-3">
          <Button type="submit" disabled={saveMut.isPending}>
            {saveMut.isPending ? "Guardando…" : "Registrar prueba"}
          </Button>
        </div>
      </form>

      {tests.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no hay pruebas registradas.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Tour</th>
                <th className="px-3 py-2">Resultado</th>
                <th className="px-3 py-2">Importe</th>
                <th className="px-3 py-2">Pago</th>
                <th className="px-3 py-2">Reserva</th>
                <th className="px-3 py-2">Reembolso</th>
                <th className="px-3 py-2">Errores / notas</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t: any) => (
                <tr key={t.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{fmtDate(t.tested_at)}</td>
                  <td className="px-3 py-2">{t.tour_label}</td>
                  <td className="px-3 py-2">
                    {t.result === "pass"
                      ? "Correcta"
                      : t.result === "partial"
                        ? "Con observaciones"
                        : "Fallida"}
                  </td>
                  <td className="px-3 py-2">
                    {t.amount != null ? `${t.amount} ${t.currency ?? ""}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">{t.payment_status ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{t.booking_status ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{t.refunded ? "Sí" : "No"}</td>
                  <td className="px-3 py-2 text-xs">
                    {t.errors && <div className="text-red-600 dark:text-red-400">{t.errors}</div>}
                    {t.notes && <div className="text-muted-foreground">{t.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => delMut.mutate(t.id)}>
                      Borrar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
